'use client'

import { useAuthorization } from '@entities/authentication'
import { useQuery } from '@tanstack/react-query'

import { getClassNpsReviewState, getPendingNps } from '../api/nps-direct.api'

export interface DeferredNpsPromptTarget {
  classId: number
}

interface UseDeferredNpsPromptTargetParams {
  enabled: boolean
}

interface UseDeferredNpsPromptTargetResult {
  target: DeferredNpsPromptTarget | null
  isPending: boolean
}

export const useDeferredNpsPromptTarget = ({
  enabled,
}: UseDeferredNpsPromptTargetParams): UseDeferredNpsPromptTargetResult => {
  const { bearerToken } = useAuthorization()

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['lesson-review', 'nps', 'pending', bearerToken],
    queryFn: async (): Promise<DeferredNpsPromptTarget | null> => {
      try {
        const pendingNps = await getPendingNps(bearerToken)
        if (!pendingNps?.classId) {
          return null
        }
        const review = await getClassNpsReviewState(bearerToken, pendingNps.classId)
        return review?.invoiceStatus === 'COMPLETED' ? { classId: pendingNps.classId } : null
      } catch {
        return null
      }
    },
    enabled,
    retry: false,
    throwOnError: false,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  return {
    target: data ?? null,
    isPending: enabled && (isPending || isFetching),
  }
}
